import React, { useEffect, useState } from "react";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, TextField,
  Typography,
} from "@mui/material";
import { resolveAhaIssuance } from "../../api/apiClient";

/**
 * Coordinator/admin decision on a card request whose result is unknown (AHA gave no clear answer). They check AHA's
 * own records on the eCards site first, then record either the card AHA shows (code, optional claim link) or that
 * AHA has no card, which makes the student selectable again. Nothing is sent to AHA from here.
 */
export default function AhaResolveDialog({ open, classId, student, onClose, onResolved }) {
  const [outcome, setOutcome] = useState("ASSIGNED");
  const [ecardCode, setEcardCode] = useState("");
  const [claimUrl, setClaimUrl] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Every opening starts clean: nothing typed for one attempt (or another student) carries over.
  useEffect(() => {
    if (!open) return;
    setOutcome("ASSIGNED");
    setEcardCode("");
    setClaimUrl("");
    setNote("");
    setError("");
  }, [open, student?.registrationId]);

  const valid = outcome === "REJECTED" || /^[0-9A-Za-z]{8,20}$/.test(ecardCode.trim());

  const close = () => {
    if (saving) return;
    setError("");
    onClose();
  };

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    try {
      const result = await resolveAhaIssuance(classId, student.registrationId, {
        outcome,
        ecardCode: outcome === "ASSIGNED" ? ecardCode.trim() : null,
        claimUrl: outcome === "ASSIGNED" && claimUrl.trim() ? claimUrl.trim() : null,
        note: note.trim() || null,
      });
      setEcardCode("");
      setClaimUrl("");
      setNote("");
      if (onResolved) onResolved(result);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to record the result. Nothing was changed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" onClick={(e) => e.stopPropagation()}>
      <DialogTitle sx={{ fontFamily: "var(--font-display)" }}>Resolve AHA result for {student?.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          AHA did not give a clear answer for this request. Look the student up on AHA's eCards site first, then
          record what AHA shows. Nothing is sent to AHA from here.
        </Typography>
        <RadioGroup value={outcome} onChange={(e) => setOutcome(e.target.value)} aria-label="What AHA shows">
          <FormControlLabel value="ASSIGNED" control={<Radio />} label="AHA shows an eCard for this student" />
          <FormControlLabel value="REJECTED" control={<Radio />}
            label="AHA has no eCard for this request (the student can be sent again)" />
        </RadioGroup>
        {outcome === "ASSIGNED" && (
          <>
            <TextField fullWidth margin="normal" label="eCard code" value={ecardCode} required
              onChange={(e) => setEcardCode(e.target.value)} inputProps={{ inputMode: "numeric", autoComplete: "off" }}
              helperText="As shown in AHA's records." />
            <TextField fullWidth margin="normal" label="Claim link (optional)" value={claimUrl}
              onChange={(e) => setClaimUrl(e.target.value)} helperText="The student's AHA claim link, if AHA shows one." />
          </>
        )}
        <TextField fullWidth margin="normal" label="Note (optional)" value={note} multiline minRows={2}
          onChange={(e) => setNote(e.target.value)} helperText="Recorded in the audit log." />
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!valid || saving}>
          {saving ? "Saving…" : "Record result"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
